/**
 * Parâmetros de busca das rotas.
 *
 * Ficam num módulo separado do `router.tsx` para que as telas possam importar
 * os tipos sem criar ciclo de importação com a árvore de rotas.
 *
 * As propriedades são OPCIONAIS de propósito: é isso que permite navegar
 * informando apenas o que muda — `navigate({ to: '/documentos', search: { page: 1 } })` —
 * em vez de obrigar cada chamada a repetir o filtro inteiro.
 *
 * A URL é entrada NÃO CONFIÁVEL. As funções de coerção abaixo transformam
 * qualquer valor inválido em `undefined`, em vez de deixar um `NaN` chegar ao
 * filtro e produzir uma consulta sem sentido.
 */

export interface LoginSearch {
  redirect?: string;
}

export interface ChangePasswordSearch {
  expired?: boolean;
  username?: string;
}

export interface MasterListSearch {
  page?: number;
  pageSize?: number;
  documentTypeId?: string;
  includeDescendantTypes?: boolean;
  status?: string[];
  search?: string;
  expiringWithinDays?: number;
  onlyPendingMyApproval?: boolean;
  onlyPendingMyAcknowledgement?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

/**
 * Tela de Administração de documentos (`ged/ged/admin` — ged.md §14.3).
 *
 * O legado tinha `filter_name`, `filter_tipo`, `filter_store_id` e
 * `filter_status`. `filter_store_id` NÃO tem equivalente aqui de propósito: a
 * organização vem do JWT e de mais lugar nenhum, então não existe parâmetro de
 * tenant na URL (R4/BUG-34). O período de criação é acréscimo — o legado
 * ordenava por `id_document DESC` e não oferecia recorte temporal.
 */
export interface DocumentAdminSearch {
  page?: number;
  pageSize?: number;
  search?: string;
  documentTypeId?: string;
  authorUserId?: string;
  status?: string[];
  /** `AAAA-MM-DD`. */
  createdFrom?: string;
  createdTo?: string;
}

/**
 * Grade de não-conformidades — sgq.md §A.8.
 *
 * `scope` é o `filtro_env_rec` do legado (`'R'` = recebidas, qualquer outra
 * coisa = emitidas), agora com um terceiro valor `ALL` que o legado não tinha.
 * `criticality` NÃO existe no filtro do servidor: a criticidade é derivada do
 * score e o recorte é feito pela faixa aqui, sobre a página carregada — está
 * documentado na própria tela para não parecer filtro de servidor.
 */
export interface NonconformitySearch {
  page?: number;
  pageSize?: number;
  scope?: string;
  status?: string[];
  type?: string[];
  source?: string[];
  departmentId?: string;
  assigneeUserId?: string;
  search?: string;
  detectedFrom?: string;
  detectedTo?: string;
  onlyRecurrent?: boolean;
  onlyOverdueCapa?: boolean;
  /** Faixa GUT (LOW/MEDIUM/HIGH). Recorte de apresentação, não do servidor. */
  criticality?: string[];
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface MeetingSearch {
  page?: number;
  pageSize?: number;
  status?: string[];
  /**
   * Filtro por TIPO, agora por identificador do catálogo e não mais pelo código
   * de um enum: os tipos são administráveis e dois deles podem ter nomes
   * parecidos, então o identificador é o que não é ambíguo.
   */
  meetingTypeIds?: string[];
  scope?: string;
  nonconformityId?: string;
  search?: string;
  startsFrom?: string;
  startsTo?: string;
  includeCancelled?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  /** `list` ou `calendar`. Preferência de visualização, preservada na URL. */
  view?: string;
}

export interface CapaActionSearch {
  page?: number;
  pageSize?: number;
  scope?: string;
  status?: string[];
  nonconformityId?: string;
  capaCaseId?: string;
  meetingId?: string;
  departmentId?: string;
  assigneeUserId?: string;
  dueFrom?: string;
  dueTo?: string;
  onlyOverdue?: boolean;
  onlyAwaitingMyVerification?: boolean;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface CircularSearch {
  page?: number;
  pageSize?: number;
  status?: string[];
  scope?: string;
  search?: string;
  publishedFrom?: string;
  publishedTo?: string;
  onlyPendingMyAcknowledgement?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface SgqIndicatorsSearch {
  from?: string;
  to?: string;
  departmentId?: string;
  source?: string[];
  type?: string[];
  bucket?: string;
  recurrenceWindowDays?: number;
}

export interface AuditSearch {
  page?: number;
  pageSize?: number;
  entityType?: string;
  entityId?: string;
  actorUserId?: string;
  action?: string[];
  occurredFrom?: Date;
  occurredTo?: Date;
  onlySigned?: boolean;
}

export function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function asBoolean(value: unknown): boolean | undefined {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
}

export function toPositiveInt(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

export function asStringArray(value: unknown): string[] | undefined {
  if (Array.isArray(value)) {
    const list = value.filter((item): item is string => typeof item === 'string');
    return list.length > 0 ? list : undefined;
  }
  if (typeof value === 'string' && value.length > 0) return [value];
  return undefined;
}

export function asDate(value: unknown): Date | undefined {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
  if (typeof value !== 'string' || value.length === 0) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}
